import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Fuel, UtensilsCrossed, Navigation, LocateFixed, Truck, BadgePercent } from "lucide-react";
import { toast } from "sonner";
import { api, getSavedPos, haversineKm, pickActive } from "@/lib/api";
import { TachoWidget } from "@/components/TachoWidget";
import { BigButton } from "@/components/BigButton";
import { PromoList, isActive } from "@/components/PromoList";
import { RouteStopsMap } from "@/components/RouteStopsMap";
import { t as tr } from "@/i18n";

const OVERPASS = ["https://overpass-api.de/api/interpreter", "https://overpass.kumi.systems/api/interpreter"];
const FOOD_RE = /trattori|camionist|tir\b|ristoro|osteria|autogrill|truck|locanda/i;

function query(kind, around, scale) {
  const a = (r) => `(around:${Math.round(r * scale)},${around})`;
  if (kind === "fuel") {
    return `[out:json][timeout:25];(nwr["highway"="services"]${a(2500)};nwr["highway"="rest_area"]${a(2000)};node["amenity"="fuel"]["hgv"="yes"]${a(2000)};node["amenity"="fuel"]${a(800)};);out center 150;`;
  }
  return `[out:json][timeout:25];(nwr["amenity"="restaurant"]${a(1200)};nwr["amenity"="fast_food"]${a(800)};);out center 150;`;
}

function routeTools(geom) {
  const cum = [0];
  for (let i = 1; i < geom.length; i++) cum.push(cum[i - 1] + haversineKm(geom[i - 1], geom[i]));
  const ahead = geom.filter((_, i) => cum[i] <= 250);
  const stride = Math.max(1, Math.ceil(ahead.length / 60));
  const around = ahead.filter((_, i) => i % stride === 0).map((p) => `${p[0].toFixed(4)},${p[1].toFixed(4)}`).join(",");
  const measure = (p) => {
    let best = 0;
    let bd = Infinity;
    geom.forEach((g, i) => {
      const d = (g[0] - p[0]) ** 2 + (g[1] - p[1]) ** 2;
      if (d < bd) {
        bd = d;
        best = i;
      }
    });
    return { along_km: cum[best], off_km: haversineKm(geom[best], p) };
  };
  return { around, measure };
}

async function fetchReal(kind, around, scale, measure) {
  for (const url of OVERPASS) {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 15000);
      const res = await fetch(url, { method: "POST", body: new URLSearchParams({ data: query(kind, around, scale) }), signal: ctrl.signal });
      clearTimeout(t);
      if (!res.ok) continue;
      const json = await res.json();
      const items = json.elements.map((e) => {
        const p = [e.lat ?? e.center?.lat, e.lon ?? e.center?.lon];
        const tg = e.tags || {};
        const name = tg.name || tg.brand || tr(tg.highway === "services" ? "Area di servizio" : tg.highway === "rest_area" ? "Area di sosta" : kind === "fuel" ? "Distributore" : "Ristorante");
        return { id: `${e.type}${e.id}`, name, lat: p[0], lng: p[1], hgv: tg.hgv === "yes" || ["services", "rest_area"].includes(tg.highway), trucker: FOOD_RE.test(name), source: "reale", ...(p[0] ? measure(p) : {}) };
      }).filter((x) => x.lat && (x.off_km == null || x.off_km <= 3));
      const base = (x) => x.along_km ?? x.distance_km;
      const score = (x) => base(x) - (kind === "fuel" ? (x.hgv ? 6 : 0) : (x.trucker ? 8 : 0));
      return items.sort((a, b) => score(a) - score(b)).slice(0, 12);
    } catch {
      /* try next */
    }
  }
  return null;
}

const PoiCard = ({ p, onGo, kind }) => (
  <div className="nv-card p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`poi-${p.id}`}>
    <div className="flex-1 min-w-0">
      <p className="font-display text-2xl font-extrabold uppercase truncate">{p.name}</p>
      <p className="text-slate-400 flex flex-wrap gap-x-3">
        <span className="text-white font-mono font-bold" data-testid={`poi-distance-${p.id}`}>{p.along_km != null ? tr("tra {km} km sul percorso", { km: Math.round(p.along_km) }) : `${p.distance_km.toFixed(1)} km`}</span>
        {p.road && <span>{p.road}</span>}
        {kind === "fuel" && p.hgv && <span className="text-[#10b981] flex items-center gap-1"><Truck className="h-4 w-4" /> {tr("Adatta ai mezzi pesanti")}</span>}
        {kind === "food" && p.trucker && <span className="text-[#f59e0b]">{tr("Consigliata per camionisti")}</span>}
        <span>{tr(p.source === "reale" ? "Dati OpenStreetMap" : "Dato di esempio")}</span>
      </p>
    </div>
    <div className="sm:w-56"><BigButton testId={`poi-go-${p.id}`} icon={Navigation} color="#ef0298" label={tr("Portami qui")} onClick={() => onGo(p)} /></div>
  </div>
);

export default function Breaks() {
  const nav = useNavigate();
  const [kind, setKind] = useState("fuel");
  const [pos, setPos] = useState(getSavedPos());
  const [trip, setTrip] = useState(undefined);
  const [items, setItems] = useState(null);
  const [stops, setStops] = useState(null);

  useEffect(() => {
    api.get("/trips").then((r) => {
      const t = pickActive(r.data);
      setTrip(t);
      if (!getSavedPos() && t?.last_position) setPos([t.last_position.lat, t.last_position.lng]);
      else if (!getSavedPos() && t) setPos([t.pickup.lat, t.pickup.lng]);
    });
  }, []);

  useEffect(() => {
    if (!trip || !pos) return;
    setStops(null);
    api.get(`/trips/${trip.id}/route-stops`, { params: { lat: pos[0], lng: pos[1] } }).then((r) => setStops(r.data)).catch(() => setStops(false));
  }, [trip, pos]);

  const routeMode = Boolean(trip && stops);
  const waiting = trip === undefined || (trip && stops === null);

  useEffect(() => {
    if (!pos || waiting) return;
    setItems(routeMode ? stops[kind === "promo" ? "promos" : kind] : null);
    if (kind === "promo") {
      if (routeMode) setItems(stops.promos);
      else api.get("/promos", { params: { lat: pos[0], lng: pos[1] } }).then((r) => setItems(r.data));
      return;
    }
    const tools = routeMode ? routeTools(stops.geometry) : null;
    const real = tools
      ? fetchReal(kind, tools.around, 1, tools.measure)
      : fetchReal(kind, `${pos[0]},${pos[1]}`, 10, (p) => ({ distance_km: haversineKm(pos, p) }));
    real.then(async (r) => {
      if (r && r.length) setItems(r);
      else if (routeMode) toast(tr("Dati reali non raggiungibili: mostro luoghi di esempio"), { id: "poi-fallback" });
      else {
        const { data } = await api.get("/pois/demo", { params: { lat: pos[0], lng: pos[1], kind } });
        setItems(data);
        toast(tr("Dati reali non raggiungibili: mostro luoghi di esempio"), { id: "poi-fallback" });
      }
    });
  }, [kind, pos, stops, routeMode, waiting]);

  const useGps = () => navigator.geolocation?.getCurrentPosition((g) => setPos([g.coords.latitude, g.coords.longitude]), () => toast.error(tr("GPS non disponibile")));
  const go = (p) => nav(`/navigatore?${new URLSearchParams({ ...(trip ? { trip: trip.id } : {}), lat: p.lat, lng: p.lng, name: p.name })}`);

  return (
    <div className="max-w-6xl mx-auto p-4 lg:p-8 grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-5 space-y-4">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase">{tr("Pausa sicura")}</h1>
        <p className="text-slate-400">{tr("Dopo 4 ore e 30 minuti di guida serve una pausa di 45 minuti. Scegli dove fermarti: calcolo il percorso per il tuo mezzo.")}</p>
        <TachoWidget compact />
        <BigButton testId="breaks-gps-btn" icon={LocateFixed} variant="outline" color="#4accff" label={tr("Usa la mia posizione GPS")} sub={pos ? tr("Posizione: {p}", { p: `${pos[0].toFixed(3)}, ${pos[1].toFixed(3)}` }) : tr("Posizione sconosciuta")} onClick={useGps} />
      </div>
      <div className="lg:col-span-7 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <BigButton testId="breaks-tab-fuel" icon={Fuel} color="#4accff" variant="outline" active={kind === "fuel"} label={tr("Stazioni di servizio")} onClick={() => setKind("fuel")} />
          <BigButton testId="breaks-tab-food" icon={UtensilsCrossed} color="#f59e0b" variant="outline" active={kind === "food"} label={tr("Punti ristoro")} onClick={() => setKind("food")} />
          <BigButton testId="breaks-tab-promo" icon={BadgePercent} color="#ef0298" variant="outline" active={kind === "promo"} label={tr("Promozioni attive")} onClick={() => setKind("promo")} />
        </div>
        {pos && (
          <p className="font-semibold" data-testid="stops-context">
            {routeMode ? tr("Sulla tratta verso {d}", { d: stops.destination }) : tr("Nessun viaggio attivo: luoghi vicini a te")}
          </p>
        )}
        {pos && !waiting && <RouteStopsMap geometry={routeMode ? stops.geometry : null} pos={pos} items={items || []} kind={kind} isActive={isActive} />}
        {pos && kind === "promo" && items && <PromoList items={items} onGo={go} />}
        {!pos && <p className="text-slate-400">{tr("Attiva il GPS per trovare i luoghi vicini.")}</p>}
        {pos && !items && <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="nv-card h-24 animate-pulse" />)}</div>}
        <div className="space-y-3" data-testid="poi-list">{kind !== "promo" && items?.map((p) => <PoiCard key={p.id} p={p} kind={kind} onGo={go} />)}</div>
      </div>
    </div>
  );
}
